import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Clock, Check, X, Camera, Repeat, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ZoomableImage } from '../../components/ZoomableImage';
import { api, photoURL } from '../../lib/api';
import { toast } from 'sonner';

const discTxt = (g, d) => (d != null && Number(d) !== Number(g)) ? `Q${Number(g ?? 0).toFixed(2)} gas / Q${Number(d).toFixed(2)} diésel` : `Q${Number(g ?? 0).toFixed(2)}`;


const LABELS = { vpower: 'V-Power', super: 'Súper', regular: 'Regular', diesel: 'Diesel', ulsd: 'ULSD' };
const ORDER = ['vpower', 'super', 'regular', 'diesel', 'ulsd'];
const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export const HH_STATUS = {
  pending: ['#fef3c7', '#92400e', 'Pendiente'],
  approved: ['#dbeafe', '#1e40af', 'Aprobado · programado'],
  approved_series: ['#e0e7ff', '#3730a3', 'Serie aprobada'],
  active: ['#dcfce7', '#166534', 'Activo'],
  awaiting_close: ['#fee2e2', '#991b1b', 'Por cerrar · falta foto'],
  closed: ['#e0e7ff', '#3730a3', 'Cerrado'],
  rejected: ['#e5e7eb', '#4b5563', 'Rechazado'],
  expired: ['#e5e7eb', '#4b5563', 'Expirado'],
  cancelled: ['#e5e7eb', '#4b5563', 'Cancelado'],
};

export const HHChip = ({ s }) => {
  const [bg, fg, label] = HH_STATUS[s] || ['#e5e7eb', '#4b5563', s];
  return <span className="text-[10.5px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ background: bg, color: fg }}>{label}</span>;
};

const PricesLine = ({ prices }) => (
  <span className="font-mono tabular-nums text-[10.5px]">
    {ORDER.filter(k => prices?.[k]?.self != null).map(k => `${LABELS[k]}: Q${prices[k].self.toFixed(2)}`).join(' · ')}
  </span>
);

export const AdminHappyHour = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState(null);       // hh en revisión
  const [discount, setDiscount] = useState('');
  const [discountDiesel, setDiscountDiesel] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [photoHH, setPhotoHH] = useState(null);
  const [hhEdit, setHhEdit] = useState({});
  const [postEdit, setPostEdit] = useState({});
  const [q, setQ] = useState('');
  const [tmF, setTmF] = useState('');
  const [statusF, setStatusF] = useState('');

  const load = () => {
    api.get('/happy-hours').then(r => setRows(r.data)).catch(() => toast.error('Error cargando Happy Hours'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const tmOptions = useMemo(() => [...new Set(rows.flatMap(r => (r.station_tm || '').split(' / ').filter(Boolean)))].sort(), [rows]);
  const STATUS_GROUPS = { programadas: ['approved', 'approved_series'], activas: ['active'], por_cerrar: ['awaiting_close'] };
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return rows.filter(r => {
      if (ql && !`${r.station_name || ''} ${r.brand || ''}`.toLowerCase().includes(ql)) return false;
      if (tmF && !(r.station_tm || '').split(' / ').includes(tmF)) return false;
      return true;
    });
  }, [rows, q, tmF]);

  const pending = filtered.filter(r => r.status === 'pending');
  const running = filtered.filter(r =>
    (statusF ? STATUS_GROUPS[statusF] : ['approved', 'approved_series', 'active', 'awaiting_close']).includes(r.status));
  const history = filtered.filter(r => ['closed', 'rejected', 'expired', 'cancelled'].includes(r.status)).slice(0, 30);

  const openReview = (hh) => { setReview(hh); setDiscount(String(hh.discount_requested ?? '')); setDiscountDiesel(hh.discount_diesel_requested != null ? String(hh.discount_diesel_requested) : ''); setNotes(''); setHhEdit({}); setPostEdit({}); };

  const suggested = useMemo(() => {
    if (!review) return {};
    const d = parseFloat(discount);
    if (isNaN(d)) return {};
    const dd = discountDiesel.trim() === '' ? null : parseFloat(discountDiesel);
    const out = {};
    Object.entries(review.vigente_prices || {}).forEach(([fid, slot]) => {
      const disc = dd != null && !isNaN(dd) && (fid === 'diesel' || fid === 'ulsd') ? dd : d;
      if (slot?.self != null) out[fid] = { self: Math.max(1, +(slot.self - disc).toFixed(2)) };
    });
    return out;
  }, [review, discount, discountDiesel]);

  const act = async (action) => {
    setSaving(true);
    try {
      const toPrices = (edits) => {
        const out = {};
        Object.entries(edits).forEach(([fid, v]) => {
          const n = parseFloat(v);
          if (!isNaN(n) && n > 0) out[fid] = { self: n };
        });
        return out;
      };
      await api.patch(`/happy-hours/${review.id}`, {
        action, discount: parseFloat(discount),
        discount_diesel: discountDiesel.trim() === '' ? null : parseFloat(discountDiesel),
        review_notes: notes,
        hh_prices: toPrices(hhEdit), post_prices: toPrices(postEdit),
      });
      toast.success(action === 'approve' ? 'Happy Hour aprobado · estación notificada' : 'Solicitud rechazada');
      setReview(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const cancelSeries = async (hh) => {
    if (!window.confirm(`¿Cancelar las fechas restantes de la serie de ${hh.station_name}?`)) return;
    try {
      const r = await api.post(`/happy-hours/series/${hh.id}/cancel`);
      toast.success(`Serie cancelada · ${r.data.cancelled_occurrences} fecha(s) restante(s) canceladas`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cancelar la serie'); }
  };

  const Row = ({ hh, actions }) => (
    <div className={`border rounded-xl p-3.5 bg-white ${hh.status === 'awaiting_close' ? 'border-[#fca5a5]' : 'border-[#e8e5de]'}`} data-testid={`hh-row-${hh.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Clock size={13} className="text-[#8a8579]"/>
        <p className="text-[13px] font-medium">{hh.station_name}</p>
        <span className="text-[11px] text-[#8a8579]">{hh.brand}</span>
        {hh.station_tm && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#e8e5de] text-[#5a5a5a]" data-testid={`hh-tm-${hh.id}`}>TM: {hh.station_tm}</span>}
        <HHChip s={hh.status}/>
        {(hh.is_series || hh.series_id) && (
          <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-[#e0e7ff] text-[#3730a3] uppercase tracking-wider font-medium" data-testid={`hh-series-badge-${hh.id}`}>
            <Repeat size={9} className="inline mr-0.5 -mt-px"/> {hh.is_series ? 'Serie' : (hh.series_label || 'Serie')}
          </span>
        )}
        <div className="flex-1"/>
        {hh.is_series && hh.status === 'approved_series' && (
          <Button variant="outline" onClick={() => cancelSeries(hh)} className="h-7 rounded-full text-[11px] border-[#991b1b]/40 text-[#991b1b]" data-testid={`hh-cancel-series-btn-${hh.id}`}>
            Cancelar serie
          </Button>
        )}
        {actions}
      </div>
      {hh.is_series ? (
        <p className="text-[11.5px] text-[#5a5a5a] mt-1">
          Los {hh.recurrence?.label} · {hh.recurrence?.start_time_gt} – {hh.recurrence?.end_time_gt} (GT)
          · {(hh.recurrence?.occurrences || []).length} fecha(s){hh.occurrences_created != null && <> · {hh.occurrences_created} programada(s){hh.occurrences_skipped ? ` · ${hh.occurrences_skipped} con traslape omitida(s)` : ''}</>}
          · Baja solicitada <strong>{discTxt(hh.discount_requested, hh.discount_diesel_requested)}</strong>
          {hh.discount_approved != null && <> · aprobada <strong>{discTxt(hh.discount_approved, hh.discount_diesel_approved)}</strong></>}
          {' '}· por {hh.requested_by_name}
        </p>
      ) : (
      <p className="text-[11.5px] text-[#5a5a5a] mt-1">
        {fmt(hh.start_at)} → {fmt(hh.end_at)} · Baja solicitada <strong>{discTxt(hh.discount_requested, hh.discount_diesel_requested)}</strong>
        {hh.discount_approved != null && <> · aprobada <strong>{discTxt(hh.discount_approved, hh.discount_diesel_approved)}</strong></>}
        {' '}· por {hh.requested_by_name}
      </p>
      )}
      {hh.hh_prices && <p className="text-[11px] text-[#166534] mt-0.5">Sugeridos HH: <PricesLine prices={hh.hh_prices}/></p>}
      {hh.post_prices && <p className="text-[11px] text-[#1e40af] mt-0.5" data-testid={`hh-post-line-${hh.id}`}>Regreso post-HH: <PricesLine prices={hh.post_prices}/>{hh.status === 'closed' ? ' · restaurados como vigentes' : ''}</p>}
      {hh.notes && <p className="text-[11px] text-[#8a8579] italic mt-0.5">"{hh.notes}"</p>}
      {hh.review_notes && <p className="text-[11px] text-[#8a8579] mt-0.5">Pricing: {hh.review_notes}</p>}
      {['active', 'awaiting_close', 'closed'].includes(hh.status) && (
        hh.start_photo_url
          ? <p className="text-[11px] text-[#166534] mt-0.5">
              Foto del cambio {fmt(hh.start_photo_at)} por {hh.start_photo_by_name}
              <button onClick={() => setPhotoHH(hh)} className="ml-2 underline underline-offset-2 inline-flex items-center gap-1" data-testid={`hh-start-photo-view-${hh.id}`}>
                <Camera size={11}/> Ver
              </button>
            </p>
          : <p className="text-[11px] text-[#991b1b] mt-0.5">Sin foto del monolito con precios HH</p>
      )}
      {hh.status === 'closed' && (
        <p className="text-[11px] text-[#3730a3] mt-1">
          Cerrado {fmt(hh.closed_at)} por {hh.closed_by_name}
          {hh.photo_url && (
            <button onClick={() => setPhotoHH(hh)} className="ml-2 underline underline-offset-2 inline-flex items-center gap-1" data-testid={`hh-photo-btn-${hh.id}`}>
              <Camera size={11}/> Ver foto
            </button>
          )}
        </p>
      )}
    </div>
  );

  if (loading) return <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>;

  return (
    <div className="space-y-5" data-testid="admin-happy-hour-panel">
      <div className="flex flex-wrap items-center gap-2" data-testid="hh-filters">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar estación…" className="h-9 pl-8 bg-white text-[12.5px] rounded-full" data-testid="hh-filter-station"/>
        </div>
        <select
          value={tmF}
          onChange={e => setTmF(e.target.value)}
          className="h-9 px-3 rounded-full border border-[#e8e5de] bg-white text-[12px] text-[#3a3a3a] focus:outline-none max-w-[200px]"
          data-testid="hh-filter-tm"
        >
          <option value="">TM: todos</option>
          {tmOptions.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={statusF}
          onChange={e => setStatusF(e.target.value)}
          className="h-9 px-3 rounded-full border border-[#e8e5de] bg-white text-[12px] text-[#3a3a3a] focus:outline-none"
          data-testid="hh-filter-status"
        >
          <option value="">Estado: todos</option>
          <option value="programadas">Programadas</option>
          <option value="activas">Activas</option>
          <option value="por_cerrar">Por cerrar</option>
        </select>
        {(q || tmF || statusF) && (
          <button onClick={() => { setQ(''); setTmF(''); setStatusF(''); }} className="text-[11px] text-[#8a8579] underline underline-offset-2" data-testid="hh-filter-clear">
            Limpiar
          </button>
        )}
      </div>
      <div>
        <h3 className="font-serif text-[20px] mb-2">Solicitudes pendientes · {pending.length}</h3>
        {pending.length === 0 ? <p className="text-[12.5px] text-[#8a8579]">Sin solicitudes pendientes.</p> : (
          <div className="space-y-2">
            {pending.map(hh => (
              <Row key={hh.id} hh={hh} actions={
                <Button onClick={() => openReview(hh)} className="h-8 rounded-full bg-[#1c1c1c] text-white text-[12px]" data-testid={`hh-review-btn-${hh.id}`}>Revisar</Button>
              }/>
            ))}
          </div>
        )}
      </div>
      <div>
        <h3 className="font-serif text-[20px] mb-2">Programados / activos / por cerrar · {running.length}</h3>
        {running.length === 0 ? <p className="text-[12.5px] text-[#8a8579]">Nada en curso.</p> : (
          <div className="space-y-2">{running.map(hh => <Row key={hh.id} hh={hh}/>)}</div>
        )}
      </div>
      <div>
        <h3 className="font-serif text-[20px] mb-2">Historial</h3>
        {history.length === 0 ? <p className="text-[12.5px] text-[#8a8579]">Sin historial.</p> : (
          <div className="space-y-2">{history.map(hh => <Row key={hh.id} hh={hh}/>)}</div>
        )}
      </div>

      <Dialog open={!!review} onOpenChange={(o) => { if (!o) setReview(null); }}>
        <DialogContent className="max-w-lg bg-[#f8f6f1]" data-testid="hh-review-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[20px]">Happy Hour · {review?.station_name}</DialogTitle></DialogHeader>
          {review && (
            <div className="space-y-4">
              <p className="text-[12.5px] text-[#5a5a5a]">
                {review.is_series
                  ? <>Serie recurrente: los <strong>{review.recurrence?.label}</strong> · {review.recurrence?.start_time_gt} – {review.recurrence?.end_time_gt} (GT) · {(review.recurrence?.occurrences || []).length} fecha(s) · solicitado por {review.requested_by_name}</>
                  : <>{fmt(review.start_at)} → {fmt(review.end_at)} · solicitado por {review.requested_by_name}</>}
                {review.notes && <> · "{review.notes}"</>}
              </p>
              <div className="flex gap-4">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">Bajar gasolinas (Q)</Label>
                  <Input type="number" step="0.25" value={discount} onChange={e => setDiscount(e.target.value)} className="mt-1 bg-white w-32" data-testid="hh-discount-input"/>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">Bajar diésel (Q)</Label>
                  <Input type="number" step="0.25" value={discountDiesel} onChange={e => setDiscountDiesel(e.target.value)} placeholder="Igual que gas." className="mt-1 bg-white w-32" data-testid="hh-discount-diesel-input"/>
                </div>
              </div>
              {review.is_series ? (
                <div className="bg-white border border-[#e8e5de] rounded-lg p-3" data-testid="hh-series-review-note">
                  <p className="text-[12px] text-[#5a5a5a]">
                    Al aprobar se programan automáticamente las {(review.recurrence?.occurrences || []).length} fecha(s) de la serie.
                    Los precios HH y de regreso de <strong>cada fecha</strong> se calculan al activarse
                    (vigente de ese día − monto), así siguen los cambios de precio del mes.
                  </p>
                </div>
              ) : (
              <div className="bg-white border border-[#e8e5de] rounded-lg p-3">
                <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579] mb-1.5">Precios del ciclo · edita si es necesario</p>
                {Object.keys(suggested).length === 0 ? (
                  <p className="text-[12px] text-[#991b1b]">La estación no tiene precios vigentes.</p>
                ) : (
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="text-[9.5px] uppercase tracking-wider text-[#8a8579]">
                        <th className="text-left font-normal py-0.5">Producto</th>
                        <th className="text-left font-normal py-0.5">Vigente</th>
                        <th className="text-left font-normal py-0.5 text-[#166534]">Precio HH</th>
                        <th className="text-left font-normal py-0.5 text-[#1e40af]">Regreso (post-HH)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ORDER.filter(k => review.vigente_prices?.[k]?.self != null).map(k => (
                        <tr key={k}>
                          <td className="py-1">{LABELS[k]}</td>
                          <td className="py-1 font-mono tabular-nums text-[#8a8579]">Q{review.vigente_prices[k].self.toFixed(2)}</td>
                          <td className="py-1 pr-2">
                            <Input type="number" step="0.01"
                              value={hhEdit[k] ?? (suggested[k]?.self?.toFixed(2) || '')}
                              onChange={e => setHhEdit(p => ({ ...p, [k]: e.target.value }))}
                              className="h-8 bg-white font-mono text-[12px] w-24" data-testid={`hh-price-${k}`}/>
                          </td>
                          <td className="py-1">
                            <Input type="number" step="0.01"
                              value={postEdit[k] ?? review.vigente_prices[k].self.toFixed(2)}
                              onChange={e => setPostEdit(p => ({ ...p, [k]: e.target.value }))}
                              className="h-8 bg-white font-mono text-[12px] w-24" data-testid={`hh-post-price-${k}`}/>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <p className="text-[10px] text-[#8a8579] mt-1.5">Al aprobar se congela el precio de regreso; cuando la estación cierre el HH con foto, el sistema restaura automáticamente esos precios como vigentes.</p>
              </div>
              )}
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#8a8579]">Notas (opcional · requerido al rechazar)</Label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} className="mt-1 bg-white min-h-[60px]" data-testid="hh-review-notes"/>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" disabled={saving} onClick={() => act('reject')} className="rounded-full text-[12.5px] border-[#991b1b]/40 text-[#991b1b]" data-testid="hh-reject-btn">
                  <X size={13} className="mr-1"/> Rechazar
                </Button>
                <Button disabled={saving || isNaN(parseFloat(discount))} onClick={() => act('approve')} className="rounded-full bg-[#15803d] hover:bg-[#166534] text-white text-[12.5px]" data-testid="hh-approve-btn">
                  {saving ? <Loader2 size={13} className="animate-spin mr-1"/> : <Check size={13} className="mr-1"/>} Aprobar y notificar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!photoHH} onOpenChange={(o) => { if (!o) setPhotoHH(null); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl bg-[#f8f6f1]" data-testid="hh-photo-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[18px]">Fotos Happy Hour · {photoHH?.station_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 overflow-y-auto">
            {photoHH?.start_photo_url && (
              <div>
                <p className="text-[11.5px] uppercase tracking-wider text-[#5a5a5a] mb-1.5">Inicio · monolito con precios HH</p>
                <ZoomableImage src={photoURL(photoHH.start_photo_url)} alt="Monolito inicio HH" maxH="45vh" testId="hh-start-photo-zoom"/>
              </div>
            )}
            {photoHH?.photo_url && (
              <div>
                <p className="text-[11.5px] uppercase tracking-wider text-[#5a5a5a] mb-1.5">Cierre · precios de regreso</p>
                <ZoomableImage src={photoURL(photoHH.photo_url)} alt="Monolito cierre" maxH="45vh" testId="hh-photo-zoom"/>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
